import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  CheckCircleOutilineIcon,
  EditIcon,
  PlusIcon,
  Skeleton,
  Text,
  TrashIcon,
} from '@mercai/clever'

import { ProductCard } from '@/components'

import { generateButtonClasses } from '@/pages/business/components/catalog-products/catalog-products.constants'

import { QuotationRequestProductsBlockProps } from './products-block.type'
import styles from './products-block.module.css'

export const QuotationRequestProductsBlockDefault = ({
  title,
  type,
  rfqProducts = [],
  errors,
  isEditable,
  catalogProducts = [],
  queryParams,
  setProductToUpdate,
  onHandleRfqRemoveCatalogProduct,
  setQueryParams,
}: QuotationRequestProductsBlockProps) => {
  const { t } = useTranslation('screenBusinessV2')

  const [selected, setSelected] = useState<number>(0)

  const onClickEditProduct = () => {
    setProductToUpdate(catalogProducts?.[selected])
    setQueryParams({
      ...queryParams,
      form: 'select-catalog-product',
    })
  }

  const onClickAddProduct = () => {
    setProductToUpdate(catalogProducts?.[selected])
    setQueryParams({
      ...queryParams,
      form: 'add-product-catalog',
      uuid: catalogProducts?.[selected]?.product?.smartCatalogProductUuid || '',
    })
  }

  const onClickDeleteProduct = async () => {
    const success = await onHandleRfqRemoveCatalogProduct(
      rfqProducts?.[selected],
    )

    if (success) {
      setSelected(0)
    }
  }

  const selectedQuotationProduct = catalogProducts?.[selected]
  const isNonCatalogedProduct = type === 'non-cataloged-product'

  return (
    <div className={styles.core}>
      <Text variant="title-medium">{title}</Text>

      <div className={styles['row-buttons']}>
        {catalogProducts?.length > 0 && (
          <div className={styles['button-container']}>
            {catalogProducts?.map((product, i) => {
              const isCompleted = !!product.catalogProductUuid

              return (
                <Button
                  key={`${product?.uuid}-${i}`}
                  model="pill"
                  variant="primary-outline"
                  className={generateButtonClasses(
                    selected === i,
                    !!errors[`products[${i}]`],
                  )}
                  onClick={() => setSelected(i)}
                >
                  {`${t('catalogProductsButton')} ${i + 1}`}
                  {isEditable && isCompleted && <CheckCircleOutilineIcon />}
                </Button>
              )
            })}
          </div>
        )}

        {isEditable && rfqProducts?.length === 0 && (
          <Text>{t('catalogProductsSelectProductDescription')}</Text>
        )}

        {isEditable && (
          <Button
            variant="light"
            onClick={() => setQueryParams({ form: 'add-catalog-product' })}
          >
            {t('catalogProductsUnstructuredProductWarningButton')}
          </Button>
        )}
      </div>

      {isNonCatalogedProduct && (
        <div>
          <Text>
            <strong>{t('catalogProductsItemIsGeneratedByAiTerm')}:</strong>{' '}
            {selectedQuotationProduct?.observation}
          </Text>
        </div>
      )}

      {catalogProducts?.length > 0 && (
        <ProductCard
          img={
            selectedQuotationProduct?.catalogProduct?.attributes
              ?.mainImage as string
          }
          title={
            selectedQuotationProduct?.catalogProduct?.attributes?.title ||
            catalogProducts?.[selected]?.product?.title ||
            '-'
          }
          category={
            selectedQuotationProduct?.catalogProduct?.category?.name ||
            catalogProducts?.[selected]?.product?.category ||
            '-'
          }
          density={
            selectedQuotationProduct?.catalogProduct?.attributes?.density ||
            catalogProducts?.[selected]?.product?.density ||
            '-'
          }
          fluidity={
            selectedQuotationProduct?.catalogProduct?.attributes?.fluidity ||
            catalogProducts?.[selected]?.product?.fluidity ||
            '-'
          }
          isSimilarAccepted={
            catalogProducts?.[selected]?.isSimilarAccepted || false
          }
          quantity={catalogProducts?.[selected]?.quantity}
          purchaseFrequency={catalogProducts?.[selected]?.purchaseFrequency}
          targetPrice={catalogProducts?.[selected]?.targetPrice}
          observation={catalogProducts?.[selected]?.observation}
          actions={
            isEditable && (
              <div className={styles['button-actions']}>
                {catalogProducts?.length > 0 && (
                  <Button
                    size="small"
                    model="square"
                    variant="secondary"
                    onClick={onClickDeleteProduct}
                  >
                    <TrashIcon />
                  </Button>
                )}

                <Button
                  size="small"
                  model="square"
                  variant="secondary"
                  onClick={onClickEditProduct}
                >
                  <EditIcon />
                </Button>

                {isNonCatalogedProduct && (
                  <Button
                    size="small"
                    model="square"
                    variant="primary"
                    onClick={onClickAddProduct}
                  >
                    <PlusIcon />
                  </Button>
                )}
              </div>
            )
          }
          isGeneratedByAi={type === 'non-cataloged-product'}
        />
      )}
    </div>
  )
}

export const QuotationRequestProductsBlockLoading = () => {
  return (
    <div className={styles.core}>
      <Skeleton width="60%" height="1rem" />

      <div className={styles['row-buttons']}>
        <Skeleton width="60%" height="1rem" />
        <Skeleton width="60%" height="1rem" />
        <Skeleton width="60%" height="1rem" />
      </div>
    </div>
  )
}

export const QuotationRequestProductsBlock = (
  props: QuotationRequestProductsBlockProps,
) => {
  if (props.isLoading) {
    return <QuotationRequestProductsBlockLoading />
  }

  return <QuotationRequestProductsBlockDefault {...props} />
}
